// INTERACTION LOG recorder — the ordered record of what a user did in a project.
//
// Quality Check's interaction replay reads this. Chat history cannot serve the
// purpose: `chatHistory` is a whole-list PUT, `tileChatHistory` is a separate
// per-tile map, neither carries a sequence number (so nothing orders a workflow
// prompt against a tile prompt), and `ChatPanel.toSaveable` drops the tool-use
// blocks that are the very thing a replay has to diff against. Manual setting
// changes were recorded nowhere at all.
//
// ## Why this is a module singleton
//
// The app has no store (`ProjectPage` owns graph state as local React state), and
// mutations originate from ~59 panels, 20+ node renderers and several 3D viewers.
// A recorder threaded through as a prop would have to touch all of them. A module
// singleton with two chokepoints — `authFetch` here, `ChatPanel`/`ProjectPage` for
// the semantic events — covers every tile without per-tile code, which is what
// makes it work regardless of template. It mirrors `client.js`'s own module-level
// `_authErrorHandler`.
//
// ## Four event roles, and why provenance decides which
//
// Agent, guided-tutorial and user mutations all route through the same `panelRefs`
// handlers, so a mutation cannot be attributed by inspection — the recorder has to
// be told who is driving. An ambient actor stack does that (`withActor`), and every
// event lands in one of four roles:
//
//   prompt                        the replay's INPUT — re-issued to the live agent
//   agent_tool                    the EXPECTATION — what the agent chose last time,
//                                 which the replay diffs the live plan against
//   http/node_data/graph, agent   the agent's EFFECT — replayed to re-align when the
//                                 live agent diverges
//   http/node_data/graph, user    a manual action — re-applied in sequence
//
// Recording the agent's effects (rather than suppressing them) is what makes
// "report the drift, then re-align to the recording" implementable at all: the
// recorded effect IS the re-alignment. It also means a per-tile tool needs no
// server-side twin — applying its recorded effect is identical to executing it —
// which is why only the seven graph tools have server-side executors
// (`tutorial_runner/graph_tools.py`) and the other 113 do not.
//
// The known cost of an ambient stack is that `executeTool` awaits, so a user action
// taken DURING an agent turn is tagged `agent` and treated as an effect rather than
// as input. The window is small (`ChatPanel.handleSend` returns early while
// `loading`, and panels disable their controls) and the consequence is mild — one
// action re-applied as a re-alignment instead of as user input.
//
// A guided or server-driven tutorial is different: it is SUPPRESSED outright, not
// tagged. A tutorial is already a deterministic script, so recording it in order to
// replay it later is circular.
//
// ## Why the transport is injected
//
// This module imports NOTHING. `api/client.js` calls `recordHttp` from inside
// `authFetch`, so importing `api/interactions.js` here would close the loop
// client -> interactionLog -> interactions -> client. ES modules would resolve it
// (all three exports are hoisted function declarations) but it is a genuine cycle
// that breaks the moment a jest test mocks one of the three. `setTransport` is the
// same inversion `client.js` already uses for `setAuthErrorHandler`, and it makes
// this module testable with a fake transport and no network at all.

// Requests that must never be recorded.
//
// `/workflow` is the 2 s autosave: a whole-graph SNAPSHOT, not an action. Its body
// is by far the largest in the app and it fires constantly, so recording it would
// dominate storage while adding nothing — graph structure is captured semantically
// instead (`create_tile`, `connect_tiles`, ... from ProjectPage), which is both
// smaller and directly replayable.
//
// `/interactions` is this recorder's own flush. Recording it would recurse.
//
// The chat-history and agent-state PUTs are transcript/bookkeeping persistence,
// not user actions; the prompts themselves are recorded explicitly by ChatPanel.
//
// `/quality-checks` is managing the suite, which is not part of the session being
// measured — recording it would put the act of registering a check inside the
// check.
export const IGNORED_PATH_PATTERNS = [
  /^\/api\/projects\/[^/]+\/workflow$/,
  /^\/api\/projects\/[^/]+\/interactions/,
  /^\/api\/projects\/[^/]+\/chat-history$/,
  /^\/api\/projects\/[^/]+\/tile-chat-history\//,
  /^\/api\/projects\/[^/]+\/workflow-agent-state$/,
  /^\/api\/quality-checks/,
];

export const FLUSH_DEBOUNCE_MS = 2000;
// Server caps a batch at 200 events / 300 KB; stay under both.
export const MAX_BATCH_EVENTS = 100;
export const MAX_BATCH_BYTES = 250_000;
// A body past this is replaced by a marker. Real config blobs (an am_input GO-MELT
// config, a build_plate layout) are tens of KB, so this only ever trips on
// something a replay could not meaningfully re-issue anyway — and it says so
// rather than silently shipping a body the server will reject.
export const MAX_EVENT_BODY_BYTES = 200_000;
// Buffer ceiling for a persistent flush failure (offline, expired session). Past
// it the OLDEST events are dropped; because `seq` is assigned at record time, the
// drop leaves a visible hole in `seq` and the replay builder reports the run
// incomplete instead of replaying a session with a silent gap.
export const MAX_BUFFERED_EVENTS = 2000;

// Injected by ProjectPage: {get(projectId), append(projectId, events)}. Unset (in
// a unit test, or before a project is open) means nothing is sent and `arm` leaves
// the recorder inert.
let transport = null;

export function setTransport(t) {
  transport = t;
}

const state = {
  projectId: null,
  readOnly: false,
  seq: 0,
  seqReady: false, // has nextSeq been adopted from the server yet?
  buffer: [],
  timer: null,
  suppressDepth: 0,
  actorStack: [],
  capped: false,
  flushing: null,
  dropped: 0,
};

function isIgnored(path) {
  return IGNORED_PATH_PATTERNS.some((re) => re.test(path));
}

/** True while a tutorial is driving — nothing at all is recorded. */
export function isSuppressed() {
  return state.suppressDepth > 0;
}

export function beginSuppress() {
  state.suppressDepth += 1;
}

export function endSuppress() {
  state.suppressDepth = Math.max(0, state.suppressDepth - 1);
}

/** Run `fn` with recording suppressed. Async-aware — spans the whole promise. */
export async function withSuppressed(fn) {
  beginSuppress();
  try {
    return await fn();
  } finally {
    endSuppress();
  }
}

function currentActor() {
  return state.actorStack.length ? state.actorStack[state.actorStack.length - 1] : null;
}

/** The actor an un-attributed event is currently credited to (`null` = the user). */
export function activeActor() {
  return currentActor()?.actor || null;
}

export function beginActor(actor, meta) {
  state.actorStack.push({ actor, meta: meta || null });
}

export function endActor() {
  state.actorStack.pop();
}

/**
 * Attribute everything recorded inside `fn` to `actor`, with `meta` naming what
 * caused it (for an agent, the tool call). Async-aware, so it spans a tool that
 * awaits a network round-trip.
 */
export async function withActor(actor, meta, fn) {
  beginActor(actor, meta);
  try {
    return await fn();
  } finally {
    endActor();
  }
}

/**
 * Start recording for a project. Adopts the server's `nextSeq` so `seq` stays
 * monotonic per PROJECT across reloads — a counter restarted at 0 would collide
 * with the stored batches and every flush would be swallowed as a duplicate.
 *
 * `readOnly` disables recording entirely: a viewer of someone else's shared
 * project must not append to the owner's log.
 */
export async function arm(projectId, { readOnly = false } = {}) {
  if (state.projectId === projectId && state.seqReady) return;
  state.projectId = projectId;
  state.readOnly = readOnly;
  state.buffer = [];
  state.seq = 0;
  state.seqReady = false;
  state.capped = false;
  state.dropped = 0;
  if (readOnly || !transport) return;
  try {
    const { nextSeq, capped } = await transport.get(projectId);
    state.seq = nextSeq || 0;
    state.capped = !!capped;
    state.seqReady = true;
  } catch {
    // A failed bootstrap must not break opening a project. Leave `seqReady` false
    // so nothing is recorded against an unknown counter — recording a session at
    // the wrong seq is worse than not recording it, because it would interleave
    // with an earlier session's events.
    state.seqReady = false;
  }
}

/** Stop recording and flush what is buffered. */
export async function disarm() {
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  await flush();
  state.projectId = null;
  state.seqReady = false;
  state.buffer = [];
}

function scheduleFlush() {
  if (state.timer) return;
  state.timer = setTimeout(() => {
    state.timer = null;
    flush().catch(() => {});
  }, FLUSH_DEBOUNCE_MS);
}

function sizeOf(value) {
  try {
    return JSON.stringify(value).length;
  } catch {
    return Infinity; // circular / unserializable → treat as oversize
  }
}

/**
 * Record one event. Never throws — recording is a side effect of using the app,
 * and a logging failure must not break the action that triggered it.
 *
 * `kind` is required. `actor` defaults to `user`; the explicit-site callers
 * (ChatPanel, ProjectPage) pass `agent` or `tutorial` themselves.
 */
export function record(event) {
  try {
    if (!state.projectId || state.readOnly || state.capped || !state.seqReady) return null;
    if (isSuppressed()) return null;
    if (!event || !event.kind) return null;

    const driver = currentActor();
    const enriched = {
      ...event,
      seq: state.seq,
      at: Date.now(),
      actor: event.actor || driver?.actor || 'user',
    };
    // An un-attributed event recorded while the agent is driving is that tool's
    // EFFECT. Naming the tool is what lets the replay re-align to it when the live
    // agent's plan diverges, and lets the timeline nest it under its tool call.
    if (!event.actor && driver?.meta?.tool) {
      enriched.effectOf = driver.meta.tool;
      if (driver.meta.seq !== undefined && driver.meta.seq !== null) {
        enriched.effectOfSeq = driver.meta.seq;
      }
    }
    if (enriched.body !== undefined && sizeOf(enriched.body) > MAX_EVENT_BODY_BYTES) {
      enriched.body = { __truncated__: true };
      enriched.truncated = true;
    }

    // Coalesce a repeat of the immediately preceding mutation on the same path.
    // Panels debounce-persist, so one edit session emits the same PUT repeatedly;
    // only the last one matters to a replay. Reuses the superseded event's seq so
    // the sequence stays dense.
    const last = state.buffer[state.buffer.length - 1];
    if (
      last &&
      last.kind === 'http' &&
      enriched.kind === 'http' &&
      last.method === enriched.method &&
      last.path === enriched.path
    ) {
      state.buffer[state.buffer.length - 1] = { ...enriched, seq: last.seq };
      scheduleFlush();
      return last.seq;
    }

    state.buffer.push(enriched);
    state.seq += 1;
    if (state.buffer.length > MAX_BUFFERED_EVENTS) {
      state.dropped += state.buffer.length - MAX_BUFFERED_EVENTS;
      state.buffer = state.buffer.slice(-MAX_BUFFERED_EVENTS);
    }
    scheduleFlush();
    return enriched.seq;
  } catch {
    return null;
  }
}

// `node.data` keys that are DERIVED state, not user intent: job results, presigned
// URLs, completeness dots, graph bookkeeping.
//
// This list exists because skipping the whole-graph autosave PUT would otherwise
// lose a whole class of real settings — a mesh edge length, a material's E and nu,
// the fluidMesh margins and every tile LABEL live only on `node.data` and have no
// config record of their own. So node.data changes are recorded as DIFFS instead,
// and this is what keeps the poll-driven writes out of them.
//
// Denying `*Url` is a CORRECTNESS matter, not just noise: replaying a recorded
// `meshUrl` would point the replay project's tile at the original project's S3
// artifact, so the run would display someone else's result instead of computing
// its own. The patterns are deliberately shape-based rather than tile-specific —
// a derived key that slips through only adds a redundant timeline row, whereas a
// real setting wrongly denied is an action the replay silently never performs.
export const DERIVED_DATA_KEY_PATTERNS = [
  /Url$/,
  /Urls$/,
  /Ready$/,
  /Results?$/,
  /Status$/,
  /^completeness$/,
  /^meta$/,
  /^stats$/,
  /^inputs$/,
  /^outputs$/,
  /^groupId$/,
  /^groupLabel$/,
  /^templateKey$/,
  /^lastJob/,
];

export function isDerivedDataKey(key) {
  return DERIVED_DATA_KEY_PATTERNS.some((re) => re.test(key));
}

/**
 * The user-meaningful subset of a `node.data` change. PURE, so it is unit-tested
 * directly. Returns null when nothing meaningful changed — which is the common
 * case, since most `node.data` writes are poll results.
 */
export function diffNodeData(prev, next) {
  if (!next) return null;
  const before = prev || {};
  const patch = {};
  for (const [key, value] of Object.entries(next)) {
    if (isDerivedDataKey(key)) continue;
    // JSON comparison: node.data values are plain JSON (they round-trip through
    // DynamoDB), so this is exact for them and cheap.
    if (JSON.stringify(before[key]) !== JSON.stringify(value)) patch[key] = value;
  }
  return Object.keys(patch).length ? patch : null;
}

/**
 * Record a `node.data` edit — the settings with no config record of their own.
 * Credited to whoever is driving, so an agent's edit is recorded as that tool's
 * effect and a user's as replay input. Skipped when only derived state changed.
 */
export function recordNodeDataChange({ nodeId, tileType, prev, next }) {
  const patch = diffNodeData(prev, next);
  if (!patch) return null;
  return record({ kind: 'node_data', nodeId, tileType, patch });
}

/**
 * Record a graph-structure change.
 *
 * Deliberately uses the ORCHESTRATOR TOOL vocabulary (`create_tile`,
 * `connect_tiles`, `delete_tile`, `rename_tile`, `delete_edge`, `clear_canvas`), so
 * one server-side executor (`tutorial_runner/graph_tools.py`) replays a user's
 * canvas edit and an agent's identically.
 *
 * Called from the shared mutators (`addNode` serves both the canvas context menu
 * and the agent's `create_tile`), so it is credited to the ambient actor: under an
 * agent it becomes that tool's effect, which the replay uses to re-align the graph
 * when the live agent builds something different.
 */
export function recordGraphEvent(event) {
  return record({ ...event, kind: event.kind || 'graph' });
}

/** Record a mutating request. Called from `authFetch`; skips reads and ignores. */
export function recordHttp({ method, path, body }) {
  if (!method || method.toUpperCase() === 'GET') return null;
  if (isIgnored(path)) return null;
  let parsed = body;
  if (typeof body === 'string') {
    try {
      parsed = JSON.parse(body);
    } catch {
      parsed = undefined; // non-JSON (FormData, blob) — the path still tells the story
    }
  } else if (body !== undefined && typeof body !== 'object') {
    parsed = undefined;
  }
  return record({ kind: 'http', method: method.toUpperCase(), path, body: parsed });
}

function splitBatches(events) {
  const batches = [];
  let current = [];
  let bytes = 0;
  for (const e of events) {
    const size = sizeOf(e);
    if (current.length && (current.length >= MAX_BATCH_EVENTS || bytes + size > MAX_BATCH_BYTES)) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(e);
    bytes += size;
  }
  if (current.length) batches.push(current);
  return batches;
}

/**
 * Send everything buffered. Serialised against itself so two concurrent flushes
 * cannot post overlapping batches. On failure the events stay buffered for the
 * next attempt.
 */
export async function flush() {
  if (state.flushing) return state.flushing;
  if (!state.projectId || state.readOnly || !transport || !state.buffer.length) return undefined;

  const projectId = state.projectId;
  const pending = state.buffer;
  state.buffer = [];

  state.flushing = (async () => {
    const unsent = [];
    for (const batch of splitBatches(pending)) {
      try {
        const res = await transport.append(projectId, batch);
        if (res && res.accepted === false) {
          // Hit the per-project ceiling. Stop recording rather than retrying
          // forever; the GET reports `capped` so the UI can say the log is full.
          state.capped = true;
          break;
        }
      } catch {
        unsent.push(...batch);
      }
    }
    if (unsent.length) {
      // Put the unsent events BACK IN FRONT of anything recorded while the flush
      // was in flight, so seq order in the log matches record order.
      state.buffer = [...unsent, ...state.buffer];
      if (state.buffer.length > MAX_BUFFERED_EVENTS) {
        state.dropped += state.buffer.length - MAX_BUFFERED_EVENTS;
        state.buffer = state.buffer.slice(-MAX_BUFFERED_EVENTS);
      }
      scheduleFlush();
    }
  })().finally(() => {
    state.flushing = null;
  });

  return state.flushing;
}

/** Test/diagnostic view of recorder state. Not used by the app. */
export function _inspect() {
  return {
    projectId: state.projectId,
    seq: state.seq,
    seqReady: state.seqReady,
    buffered: state.buffer.length,
    suppressDepth: state.suppressDepth,
    capped: state.capped,
    dropped: state.dropped,
    readOnly: state.readOnly,
    pending: state.buffer.map((e) => ({ ...e })),
  };
}

/** Test-only hard reset. */
export function _reset() {
  if (state.timer) clearTimeout(state.timer);
  Object.assign(state, {
    projectId: null,
    readOnly: false,
    seq: 0,
    seqReady: false,
    buffer: [],
    timer: null,
    suppressDepth: 0,
    capped: false,
    flushing: null,
    dropped: 0,
  });
}
